import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the Essor savings badge in sync with the selected variant.
 *
 * On the product page a variant change only morphs the variant picker. The picker dispatches a
 * product select event whose promise resolves with the fetched page, so this component copies
 * its own block's markup (text and hidden state) from that response, like `product-price` does.
 *
 * @extends {Component}
 */
class EssorSavingsBadge extends Component {
  /** @type {AbortController | undefined} */
  #listenerController;

  connectedCallback() {
    super.connectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = new AbortController();

    const scope = this.closest('.shopify-section, dialog');
    scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#listenerController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenerController?.abort();
    this.#listenerController = undefined;
  }

  /**
   * Updates the badge from the page HTML fetched for the newly selected variant.
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    /** @type {any} */
    let detail;
    try {
      ({ detail } = await event.promise);
    } catch (error) {
      // A newer selection aborts the older request; only report real failures.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[essor-savings-badge] Product select failed:', error);
      }
      return;
    }

    if (!this.isConnected || !(detail?.html instanceof Document)) return;

    if (detail.newProduct?.id) {
      this.dataset.productId = detail.newProduct.id;
    } else if (detail.productId && detail.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const source = detail.html.querySelector(`essor-savings-badge[data-block-id="${CSS.escape(blockId)}"]`);
    if (!(source instanceof HTMLElement)) return;

    this.hidden = source.hidden;
    this.replaceChildren(...Array.from(source.childNodes, (node) => document.importNode(node, true)));
  };
}

if (!customElements.get('essor-savings-badge')) {
  customElements.define('essor-savings-badge', EssorSavingsBadge);
}
